import React, {useState, useMemo} from 'react';
import {
  FormGroup,
  Intent,
} from '@blueprintjs/core';

import {IItem, SimpleMultiSelect, SimpleSelect} from '../../../../../select';
import {hoursOptions} from '../selectOptions';
import {buildByHourInterval, getByHourInterval} from '../utils';
import {OnChangeCallback} from './types';

interface ByHourProps {
  byHour?: number[];
  onChange: OnChangeCallback;
}

interface SelectHourProps {
  label?: string;
  value: string;
  onChange: (item: IItem) => void;
}

const SelectHour = ({
  label,
  value,
  onChange,
}: SelectHourProps) => {
  return (
    <>
      {label && <span className="by-hour-interval__label">{label}</span>}
      <SimpleSelect
        selected={value}
        disabled={false}
        onChange={onChange}
        options={hoursOptions}
        buttonProps={{
          minimal: false,
        }}
        popoverProps={{
          usePortal: true,
          portalClassName: 'select-hour-portal'
        }}
      />
    </>
  );
};


export const ByHour = ({
  byHour,
  onChange,
}: ByHourProps) => {
  /*
   * Select between hours from:until
   */
  const byHourInterval = getByHourInterval(byHour);
  const allDay = (!byHour || (!!byHour && byHour.length === 0));
  /*const [checked, setChecked] = useState(allDay);*/
  const [fromHour, setFromHour] = useState(allDay ? '0' : byHourInterval.from);
  const [untilHour, setUntilHour] = useState(allDay ? '23' : byHourInterval.until);
  const [error, setError] = useState<string>('');

  const _onChange = (_from: number, until: number) => {
    if (_from === 0 && until === 23) {
      // all day - this is the default for hourly schedules
      onChange('byHour')([]);
    } else {
      onChange('byHour')(buildByHourInterval(_from, until));
    }
  }

  const onSetFromHour = (item: IItem) => {
    const value = parseInt(item.value);
    const _untilHour = parseInt('' + untilHour);

    if (value > _untilHour) {
      setError(`'From' hour ${value} cannot be after 'to' hour ${_untilHour}`);
      return;
    }
    setError('');

    setFromHour(value);
    _onChange(value, _untilHour);
  };

  const onSetUntilHour = (item: IItem) => {
    const value = parseInt(item.value);
    const _fromHour = parseInt('' + fromHour);

    if (value < _fromHour) {
      setError(`'To' hour ${value} cannot be before 'from' hour ${_fromHour}`);
      return;
    }
    setError('');

    setUntilHour(value);
    _onChange(_fromHour, value);
  };

  return (
    <div className="by-hour-interval">
      <FormGroup
        label="Between"
        inline={true}
        helperText={error ? error : undefined}
        intent={error ? Intent.DANGER : undefined}
      >
        <SelectHour
          value={'' + fromHour}
          onChange={onSetFromHour}
        />
        <SelectHour
          label="and"
          value={'' + untilHour}
          onChange={onSetUntilHour}
        />
      </FormGroup>
    </div>
  )
};

export const BySpecificHours = ({
  byHour,
  onChange,
}: ByHourProps) => {
  const defaultSelected: IItem[] = useMemo(() => byHour?.map((h: number) => ({
    value: '' + h,
    label: '' + h,
  })) || [], [])

  const _onChange = (values: IItem[]) => {
    onChange('byHour')(
      values.map((v: any) => parseInt(v.value)).sort()
    );
  }

  return (
    <div className="by-hour-interval">
      <FormGroup
        label="Select Hour"
        inline={true}
      >
        <SimpleMultiSelect
          selected={defaultSelected}
          options={hoursOptions}
          onChange={_onChange}
          disabled={false}
          popoverProps={{
            usePortal: true,
            portalClassName: 'select-specific-hour-portal',
          }}
        />
      </FormGroup>
    </div>
  )
};
